import React, { useEffect, useMemo } from "react";
import { Box, Card, CardContent, useTheme } from "@mui/material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import FormTextField from "../../../../../views/components/common/form/FormTextField";
import PreviewHeader from "./PreviewHeader";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";

interface LoginPageProps {
  deviceView: "desktop" | "tablet" | "mobile";
  isStandalonePreview?: boolean; // Option to indicate standalone preview (new tab)
}

const LoginPage: React.FC<LoginPageProps> = ({
  deviceView,
  isStandalonePreview = false,
}) => {
  const theme = useTheme();

  const { swagStoreStore } = useStores();

  const loginImage = swagStoreStore.settings.loginPage.loginPageImage;

  const loginImageSource = useMemo(() => {
    if (loginImage instanceof File) {
      return {
        src: URL.createObjectURL(loginImage),
        isObjectUrl: true,
      };
    }

    if (typeof loginImage === "string" && loginImage.trim()) {
      return {
        src: loginImage,
        isObjectUrl: false,
      };
    }

    return {
      src: "https://developmentprocureasy.s3.ap-south-1.amazonaws.com/products/loginpage_1754639867409.png",
      isObjectUrl: false,
    };
  }, [loginImage]);

  useEffect(() => {
    return () => {
      if (loginImageSource.isObjectUrl) {
        URL.revokeObjectURL(loginImageSource.src);
      }
    };
  }, [loginImageSource]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: isStandalonePreview ? "100vh" : "100%",
        minHeight: isStandalonePreview ? "100vh" : "auto",
        width: "100%",
        backgroundColor: theme.palette.customColors.white,
      }}
    >
      {/* Header - Common component */}
      <PreviewHeader />

      {/* Split Layout: Image on Left, OTP Card on Right */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: deviceView === "mobile" ? "column" : "row",
          height: isStandalonePreview
            ? deviceView === "mobile"
              ? "auto"
              : "calc(100vh - 73px)"
            : deviceView === "mobile"
            ? "auto"
            : "calc(100% - 73px)",
          overflow: "hidden",
        }}
      >
        {/* Left Side - Image/Illustration (2/3 width) */}
        <Box
          sx={{
            flex:
              deviceView === "mobile"
                ? "0 0 0"
                : deviceView === "tablet"
                ? "0 0 50%"
                : "0 0 50%",
            display: deviceView === "mobile" ? "none" : "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: theme.palette.customColors.lightGrayBackground,
            position: "relative",
            overflow: "hidden",
            minHeight: deviceView === "mobile" ? 0 : "auto",
          }}
        >
          <Box
            component="img"
            src={loginImageSource.src}
            alt="Login illustration"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
        </Box>

        {/* Right Side - OTP Login Card (1/3 width) */}
        <Box
          sx={{
            flex: deviceView === "mobile" ? "1 1 auto" : "0 0 50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: deviceView === "mobile" ? 2 : deviceView === "tablet" ? 3 : 4,
            backgroundColor: theme.palette.customColors.white,
            width: deviceView === "mobile" ? "100%" : "auto",
            minHeight: deviceView === "mobile" ? "auto" : "100%",
          }}
        >
          <Card
            sx={{
              width: "100%",
              maxWidth: deviceView === "mobile" ? "100%" : "450px",
              borderRadius: "12px",
              boxShadow: `0 8px 24px ${theme.palette.customColors.shadowLight}`,
              p: deviceView === "mobile" ? 1.5 : 2,
            }}
          >
            <CardContent sx={{ p: 1, paddingBottom: "10px !important" }}>
              <CustomTypography
                variant="heading4"
                fontWeight={700}
                sx={{ mb: 1, textAlign: "left" }}
              >
                {swagStoreStore.settings.loginPage.headingText ||
                  "Login with OTP"}
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.text.secondary}
                sx={{ mb: 3, textAlign: "left" }}
                style={{
                  marginTop: "10px",
                }}
              >
                {swagStoreStore.settings.loginPage.subheadingText ||
                  "Enter your registered email address, and we'll send you a OTP to log in securely."}
              </CustomTypography>

              <Box>
                <FormTextField
                  label="Email *"
                  placeholder="Enter your email"
                  sx={{
                    width: "100%",
                    marginTop: "20px",
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor:
                          swagStoreStore.settings.branding
                            ?.textFieldActiveColor || "#6455ED",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor:
                          swagStoreStore.settings.branding
                            ?.textFieldActiveColor || "#6455ED",
                      },
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: theme.palette.grey[500],
                      opacity: 1,
                    },

                    marginBottom: "20px",
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent:
                    deviceView === "mobile" ? "center" : "flex-end",
                }}
              >
                <AppButton
                  label="SEND OTP"
                  onClick={() => {}}
                  variant="primaryBlock"
                  customStyles={{
                    width: deviceView === "mobile" ? "100%" : "40%",
                    borderRadius: "8px",
                    padding: "10px",
                    backgroundColor:
                      swagStoreStore.settings.branding?.primaryColor ||
                      theme.palette.customColors.purple,
                    color: theme.palette.customColors.white,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    "&:hover": {
                      backgroundColor:
                        swagStoreStore.settings.branding?.secondaryColor ||
                        theme.palette.customColors.purple,
                    },
                  }}
                />
              </Box>

              <Box sx={{ mt: 3, textAlign: "center" }}>
                <CustomTypography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  Don't have an account?{" "}
                  <Box
                    component="span"
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      cursor: "pointer",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Register here
                  </Box>
                </CustomTypography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default observer(LoginPage);
